import { useRef } from 'react';
import { createSubmitGuard, newAttemptKey } from '../components/projects/submitGuardModel';

/**
 * DQ-120 / DQ-158 / DQ-159 (the "double click" family) — one submit at a time.
 *
 * `mutation.isPending` turns true only on the NEXT render, so two clicks in the
 * same moment both reach `mutate()`. This guard locks synchronously (a ref), and
 * carries the `Idempotency-Key` of the attempt, so the server also answers a
 * repeated request with the first answer (utils/idempotency.ts).
 *
 *   const once = useSubmitOnce();
 *   const submit = () => { if (!once.enter()) return; mutation.mutate(vars); };
 *   // mutationFn: api.x(vars, once.key)
 *   // onSettled: (_d, err) => settleSubmit(once, err)
 */
export type SubmitOnce = ReturnType<typeof createSubmitGuard>;

export function useSubmitOnce(): SubmitOnce {
  const ref = useRef<SubmitOnce | null>(null);
  if (!ref.current) ref.current = createSubmitGuard(newAttemptKey);
  return ref.current;
}

/**
 * The attempt has settled: unlock. A real answer (success, or an error the server
 * sent) makes the next attempt a new one; no answer at all (network) keeps the key,
 * so a retry of a request that did go through is answered from the server's record.
 */
export function settleSubmit(guard: SubmitOnce, err?: unknown): void {
  guard.leave();
  if (!err || (err as { response?: unknown })?.response) guard.renewKey();
}
